import { create } from 'zustand';
import type { User } from 'markcat-domain';
import { clearAssetCache } from 'markcat-api-client';

// Refresh token lives in an HttpOnly cookie set by the server (cloud mode).
// The web app never reads or writes it; we only persist the access token in
// localStorage so reloads don't bounce to /login while the cookie is still
// valid.
interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;

  setAuth: (user: User, accessToken: string) => void;
  setAccessToken: (token: string) => void;
  /** Full session teardown — token, store state, and every image of the
   *  outgoing account. The terminal auth-failure path routes through here. */
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: localStorage.getItem('mc_access_token'),
  isAuthenticated: !!localStorage.getItem('mc_access_token'),

  setAuth: (user, accessToken) => {
    localStorage.setItem('mc_access_token', accessToken);
    set({ user, accessToken, isAuthenticated: true });
  },

  setAccessToken: (token) => {
    localStorage.setItem('mc_access_token', token);
    set({ accessToken: token });
  },

  logout: () => {
    localStorage.removeItem('mc_access_token');
    // Purges the object URLs and the CacheStorage entries the service worker's
    // CacheFirst route wrote: those carry no Vary, so an entry matches any
    // request to that URL regardless of Authorization, and the next account on
    // this browser profile would be served the previous one's tiles.
    clearAssetCache();
    set({ user: null, accessToken: null, isAuthenticated: false });
  },
}));
